import instantsearch from 'instantsearch.js';
import { searchClient } from '../algolia/client';
import { getSearchConfig, hasRequiredConfig } from './config';
import { createRouting } from './routing';
import { createWidgets } from './widgets';

const TABLET_LARGE_BREAKPOINT = 1024;

function applyDocumentTitle(title) {
  if (typeof document === 'undefined' || !title) {
    return;
  }

  document.title = title;
}

function getPanelKey(panel) {
  const container = panel ? panel.closest('[data-panel-key]') : null;

  return container ? container.getAttribute('data-panel-key') : '';
}

function applyPanelState(panel, expanded) {
  if (!panel) {
    return;
  }

  panel.classList.toggle('search-page__panel--collapsed', !expanded);

  const trigger = panel.querySelector('.search-page__filter-trigger');
  const body = panel.querySelector('.ais-Panel-body');

  if (trigger) {
    trigger.setAttribute('aria-expanded', String(expanded));
  }

  if (body) {
    if (!body.id) {
      const key = getPanelKey(panel) || 'filter-panel';
      body.id = `search-filter-panel-${key}`;
    }

    if (trigger) {
      trigger.setAttribute('aria-controls', body.id);
    }

    body.hidden = !expanded;
  }
}

function isMobileViewport() {
  return window.innerWidth < TABLET_LARGE_BREAKPOINT;
}

function setupSearchPageUi(root, search) {
  const panelState = new Map();
  const filtersToggle = root.querySelector('[data-instant-search-toggle-filters]');
  const drawer = root.querySelector('[data-search-drawer]');
  const drawerBackdrop = root.querySelector('[data-search-drawer-backdrop]');
  const drawerClose = root.querySelector('[data-search-drawer-close]');

  const setFiltersHidden = (hidden) => {
    root.classList.toggle('search-page--filters-hidden', hidden);

    if (filtersToggle && !isMobileViewport()) {
      filtersToggle.textContent = hidden ? 'Show Filters' : 'Hide Filters';
      filtersToggle.setAttribute('aria-expanded', String(!hidden));
    }
  };

  const openDrawer = () => {
    root.classList.add('search-page--drawer-open');
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    root.classList.remove('search-page--drawer-open');
    document.body.style.overflow = '';
  };

  if (isMobileViewport()) {
    setFiltersHidden(true);
    if (filtersToggle) {
      filtersToggle.textContent = 'Filters';
      filtersToggle.setAttribute('aria-expanded', 'false');
    }
  }

  const syncResponsiveUi = () => {
    if (isMobileViewport()) {
      setFiltersHidden(true);
      closeDrawer();

      if (filtersToggle) {
        filtersToggle.textContent = 'Filters';
        filtersToggle.setAttribute('aria-expanded', String(root.classList.contains('search-page--drawer-open')));
      }

      return;
    }

    closeDrawer();
    setFiltersHidden(false);
  };

  if (filtersToggle) {
    filtersToggle.addEventListener('click', () => {
      if (isMobileViewport()) {
        if (root.classList.contains('search-page--drawer-open')) {
          closeDrawer();
        } else {
          openDrawer();
        }

        filtersToggle.setAttribute('aria-expanded', String(root.classList.contains('search-page--drawer-open')));
      } else {
        setFiltersHidden(!root.classList.contains('search-page--filters-hidden'));
      }
    });
  }

  if (drawerClose) {
    drawerClose.addEventListener('click', () => {
      closeDrawer();

      if (filtersToggle && isMobileViewport()) {
        filtersToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  if (drawerBackdrop) {
    drawerBackdrop.addEventListener('click', () => {
      closeDrawer();

      if (filtersToggle && isMobileViewport()) {
        filtersToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('.search-page__filter-trigger');

    if (!trigger || !root.contains(trigger)) {
      return;
    }

    const panel = trigger.closest('.ais-Panel');
    const panelKey = getPanelKey(panel);
    const isExpanded = trigger.getAttribute('aria-expanded') !== 'true';

    if (panelKey) {
      panelState.set(panelKey, isExpanded);
    }

    applyPanelState(panel, isExpanded);
  });

  search.on('render', () => {
    root.querySelectorAll('[data-panel-key] .ais-Panel').forEach((panel) => {
      const panelKey = getPanelKey(panel);
      const expanded = panelKey && panelState.has(panelKey) ? panelState.get(panelKey) : true;

      applyPanelState(panel, expanded);
    });
  });

  window.addEventListener('resize', syncResponsiveUi);
  syncResponsiveUi();
}

function initInstantSearchPage() {
  const root = document.querySelector('[data-instant-search-root]');

  if (!root) {
    return;
  }

  if (root.dataset.instantSearchMounted) {
    return;
  }

  const config = getSearchConfig(root);

  if (!hasRequiredConfig(config)) {
    console.warn('InstantSearch page is missing Algolia configuration.');
    return;
  }

  root.dataset.instantSearchMounted = 'true';
  applyDocumentTitle(config.documentTitle);

  const search = instantsearch({
    indexName: config.indexName,
    searchClient,
    routing: createRouting(config),
  });

  search.addWidgets(createWidgets(root, config));
  setupSearchPageUi(root, search);

  let hasRendered = false;
  search.on('render', () => {
    if (!hasRendered) {
      const hitsContainer = root.querySelector('[data-instant-search-hits]');
      const hasContent = hitsContainer && hitsContainer.querySelector('.ais-Hits-item, .ais-Hits--empty');

      if (hasContent) {
        hasRendered = true;
        root.setAttribute('data-instant-search-initialized', 'true');
      }
    }
    applyDocumentTitle(config.documentTitle);
  });

  search.start();
}

export default initInstantSearchPage;
